// Utvikling til Strid (UtS/HPBP) — planprosess-panel.
//
// DESIGNPROBLEMET: fem trinn, åtte faste variabler, to sjekklister på fem punkt
// hver, IPB, oppgavetyper og sju delprodukter. Lagt ut som ett skjema blir det
// en vegg av felt ingen stab orker å fylle ut under tidspress. Løsningen her
// hviler på fire valg:
//
//  1. ÉN TING AV GANGEN. Trinnene er en skinne øverst; bare det aktive trinnet
//     er utvidet. Variablene i trinn 2 er en akkordeon der bare den du jobber
//     med er åpen (progressiv avdekking).
//  2. «NESTE HANDLING» ØVERST, STORT. Det viktigste et stabsverktøy kan svare
//     på under tidspress er «hva gjør jeg nå?». Den linjen er alltid synlig og
//     er hentet fra `fremdrift()` i @skade/hildr — ikke en egen sannhet her.
//  3. MASKINEN FYLLER DET MASKINEN KAN. Der terrengmotoren eller MET faktisk
//     kan svare (KAVOD O/D, VTSSN), står det en «hent»-knapp. Der den IKKE kan
//     (KAVOD A/V = taktiske vurderinger), sier raden det rett ut i stedet for å
//     se ut som den venter på et tall.
//  4. MANGLER ER FØRSTEKLASSES INNHOLD, ikke en valideringsfeil på slutten.
//     Hvert trinn og hver variabel bærer sin egen mangel-teller.
//
// Følger UX_DESIGNSYSTEM.md: kun tokens, SVG-ikoner (ingen emoji eller
// Unicode-glyfer), wm-window med `×` i header, tomtilstander med veiledning,
// ærlighet om hva som krever backend. All feltdata settes med textContent
// (aldri innerHTML).
//
// KJENT MANGEL: panelet har ingen `⤢` pop-out. Designsystem §4 gir ⤢ til
// paneler som har en frittstående mount; denne har ikke det. WindowManager
// setter inn den standardiserte `.wm-popout`-knappen automatisk så snart
// planprosessen legges inn i POPOUTS + POPOUT_KEY_BY_CLASS med en
// `mountPlanprosessPanel` — det krever endringer i main.ts/panels/popout.ts.
// Fram til da er ⤢ bevisst utelatt framfor å vise en knapp som ikke virker.
import {
  FASTE_VARIABLER, FAST_VARIABEL_NAVN, KAVOD, METTCR_BOKSTAV, UTS_TRINN,
  LENDE_PERSPEKTIV, LENDE_PERSPEKTIV_NAVN, VAERFAKTORER, vindretningNavn, vindstyrke,
  byggVarslingsordre, fordelTid, fremdrift, manglerForTrinn, nyPlanprosess,
  tidsplanKomplett, trinnFullfort, varslingsordreTilTekst,
  type FastVariabel, type Handlemate, type Planprosess, type UtsTrinnNr,
} from '@skade/hildr';
import { sunTimes } from '@skade/contracts';
import { termiskInnhentingsvindu, type TermiskVindu } from '@skade/terrain';

export interface PlanprosessHooks {
  /** Send varslingsordre på samband. Returner false hvis sending feilet. */
  sendVo?: (tekst: string, nr: 1 | 2) => boolean | void;
  varsle?: (tekst: string) => void;
  /** Værdata for kartsenteret (MET) → fyller VTSSN-radene med tallgrunnlag. */
  hentVaer?: () => Promise<Record<string, unknown> | null>;
  /** Terrengsvar for KAVOD O/D — det motoren faktisk kan regne ut. */
  hentLende?: (faktor: string) => Promise<string | null>;
  /**
   * Kartsenteret, til sol- og tussmørkeberegning. Returnerer null når kartet
   * ikke er klart — og da SKAL sol-blokka si at posisjonen mangler, ikke vise
   * tider for et oppdiktet punkt. Sol står opp 40 minutter senere i Kirkenes
   * enn i Kristiansand samme dag.
   */
  hentPosisjon?: () => { lat: number; lon: number } | null;
  /** Åpne ordre-panelet med valgt handlemåte (trinn 4 → fempunktsordre). */
  apneOrdre?: () => void;
  now?: () => number;
}

/** Ikoner: monokrome inline-SVG, arver currentColor (designsystem §6). */
const svg = (d: string, fill = false) =>
  `<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">`
  + `<path d="${d}" ${fill ? 'fill="currentColor"' : 'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"'}/></svg>`;

export const IKON_HAKE = svg('M4 10.5l4 4 8-9');
export const IKON_SIRKEL = svg('M10 3.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13z');
export const IKON_VARSEL = svg('M10 3.5l6.5 12h-13L10 3.5zM10 8v3.5M10 13.2v.6', false);
export const IKON_TERRENG = svg('M2 15l5-7 3.5 4.5L13 9l5 6H2z');
/** Sky — VTSSN hentes fra MET, ikke fra terrengmotoren. */
export const IKON_VAER = svg('M6 14.5h8a3 3 0 000-6 4.5 4.5 0 00-8.6 1.4A2.4 2.4 0 006 14.5z');
export const IKON_PLAN = svg('M4 3.5h9l3 3v10H4zM13 3.5v3h3M6.5 10h7M6.5 12.5h7');
export const IKON_LISTE = svg('M4 6h12M4 10h12M4 14h8');

/** Panel-ikon til verktøylinja (dock). */
export const PLANPROSESS_IKON = IKON_PLAN;

/**
 * Korte trinnavn til skinnen. Håndbokas fulle navn (UTS_TRINN[].navn) er opptil
 * 33 tegn; fem av dem i en 520px-skinne ble 974px bredt og skjøv trinn 3-5
 * usynlig ut i en horisontal rulling — altså «klippet» etter designsystem §5.
 * Skinnen viser derfor kortnavn, mens det FULLE navnet står som overskrift i
 * trinnkroppen og i knappens aria-label/tooltip. Ingen informasjon går tapt.
 */
export const TRINN_KORT: Record<UtsTrinnNr, string> = {
  1: 'Mottak', 2: 'Analyse', 3: 'Handlemåter', 4: 'Plan & ordre', 5: 'Kontroll',
};

/**
 * Er delproduktet levert? Eksplisitt per delprodukt fra UTS_TRINN — IKKE en
 * tekstlikhet mot mangel-listen. Den forrige varianten sammenlignet strenger og
 * meldte «Støttebehov», «Innhentingsplan», «Ferdig plan», «Koordinering» og
 * «Varslingsordre 2» som LEVERT på en helt tom prosess, fordi mangelteksten
 * ikke inneholdt delproduktnavnet. Det bryter §10 (ærlighet).
 */
export function delproduktLevert(navn: string, p: Planprosess): boolean {
  const a = p.analyse;
  switch (navn) {
    case 'Varslingsordre 1 (VO 1)': return !!p.vo1SendtTsMs;
    case 'Oppdrag til egen avdeling nedskrevet': return !!a.oppdragTilEgenAvdeling;
    case 'Reformulert oppdrag': return !!a.reformulertOppdrag;
    case 'Bindinger og begrensninger uthentet':
      // Enten-eller: en ordre kan mangle det ene og ha det andre, og da ER
      // uthentingen gjort. Å kreve begge ville meldt mangel på noe som ikke finnes.
      return a.bindinger.length > 0 || a.begrensninger.length > 0;
    case 'Værets påvirkning på lendet': return !!a.vaer.paavirkning;
    case 'Delkonklusjon lende': return !!a.lende.delkonklusjon;
    case 'Lendekalk': return !!a.lende.lendekalkRef;
    case 'Delkonklusjon fienden': return !!a.fiende.delkonklusjon;
    case 'Innhentings-/rekognoseringsplan': return a.fiende.etterretningsbehov.length > 0;
    case 'Støttebehov': return a.stottebehov.length > 0;
    case 'Egne ressurser analysert': return !!a.egneRessurser;
    case 'Tidsanalyse': return !!a.tid;
    case 'Sivile forhold analysert': return !!a.sivileForhold;
    case 'Taktisk risiko vurdert': return !!a.risiko.taktisk;
    case 'Ulykkesrisiko vurdert': return !!a.risiko.ulykke;
    case 'Handlemåter': return p.handlemater.length > 0;
    case 'Valgt handlemåte med plan': return !!p.valgtHmId;
    case 'Varslingsordre 2 (VO 2)': return !!p.vo2SendtTsMs;
    case 'Rekognoseringsresultat': return p.rekognoseringGjennomfort;
    case 'Ferdig plan': return p.planFerdigstilt;
    case 'Ordre (fempunktsordre)': return !!p.ordreGittTsMs;
    case 'Tilbakelesning': return p.tilbakelesningFraUndergitte;
    case 'Koordinering': return p.koordinert;
    case 'Justeringer': return p.justeringer.length > 0;
    // Ukjent delprodukt (nytt navn i HILDR) meldes som IKKE levert framfor å
    // gjette grønt — feilen skal peke mot manglende kobling, ikke skjules.
    default: return false;
  }
}

export class PlanprosessPanel {
  readonly el: HTMLElement;
  onStateChange?: () => void;

  private p: Planprosess;
  /** Hvilket trinn som er utvidet (kan avvike fra der staben «står»). */
  private apentTrinn: UtsTrinnNr = 1;
  /** Hvilken fast variabel som er utvidet i trinn 2. */
  private apenVariabel: FastVariabel | null = 'oppdrag';
  private kropp!: HTMLElement;
  private skinne!: HTMLElement;
  private neste!: HTMLElement;

  constructor(private hooks: PlanprosessHooks = {}) {
    this.p = nyPlanprosess(`UTS-${new Date(this.hooks.now?.() ?? Date.now()).toISOString().slice(0, 10)}`);
    this.el = this.bygg();
    this.render();
  }

  // ── Offentlig API ─────────────────────────────────────────────────────────
  //
  // ÅPEN-TILSTANDEN LESES FRA DOM-EN, IKKE FRA ET EGET FLAGG.
  // Panelet hadde tidligere `private open` i tillegg til CSS-klassen `.open`.
  // To kilder til samme sannhet betyr at de kan gå fra hverandre: lukker noe
  // ANNET enn `toggle()` panelet (Escape via WindowManager, én-skjerm-modus som
  // fjerner `.open` fra de andre vinduene), tror flagget fortsatt «åpen», og
  // neste klikk på dock-knappen setter det bare til «lukket» — panelet krever da
  // to klikk for å komme fram, eller ser ut som det ikke kan lukkes i det hele
  // tatt. Med DOM-en som eneste kilde kan de ikke divergere.
  toggle() { this.settApen(!this.erApen()); }

  /**
   * Tegn panelet om utenfra. Finnes fordi et poppet-ut panel får kartsenteret
   * asynkront fra hovedvinduet, og sol-blokka må males på nytt når det kommer.
   * `render` er privat med vilje — panelet eier sin egen DOM.
   */
  oppdater() { if (this.erApen()) this.render(); }
  settApen(pa: boolean) {
    this.el.classList.toggle('open', pa);
    if (pa) this.render();
    this.onStateChange?.();
  }
  erApen(): boolean { return this.el.classList.contains('open'); }
  prosess(): Planprosess { return this.p; }
  /** Antall kritiske mangler — dock-knappen viser dette som teller. */
  kritiskeMangler(): number { return fremdrift(this.p).kritiskeMangler; }

  // ── Skjelett ──────────────────────────────────────────────────────────────
  private bygg(): HTMLElement {
    const p = el('div', 'report-composer plan-panel');

    const hode = el('div', 'report-head');
    hode.append(spenn('report-title', 'Utvikling til Strid'));
    const undertekst = el('span', 'plan-head-sub');
    undertekst.textContent = 'HPBP · kompani/eskadron';
    hode.append(undertekst);
    // Header-kluster (designsystem §4): pop-out-knappen (⤢) settes inn av
    // WindowManager som `.wm-popout`, og BARE for paneler som har en ekte
    // frittstående mount i POPOUT_KEY_BY_CLASS. Planprosessen HAR nå det
    // (`plan-panel` → `planprosess`, `mountPlanprosessPanel`), så knappen
    // kommer automatisk hit, til venstre for ×. Derfor finnes det ikke — og
    // skal ikke finnes — en egen «på egen skjerm»-oppføring i verktøylinja:
    // ETT mønster for «eget vindu», i tittellinja der vinduet er (FUNN 15).
    const lukk = knapp('report-x', '×', 'Lukk', () => this.toggle());
    hode.append(lukk);
    p.append(hode);

    // «Neste handling» — det viktigste elementet i hele panelet.
    this.neste = el('button', 'plan-neste');
    this.neste.setAttribute('aria-live', 'polite');
    this.neste.onclick = () => {
      const f = fremdrift(this.p);
      this.apentTrinn = f.trinn;
      if (f.trinn === 2) this.apenVariabel = this.forsteApneVariabel();
      this.render();
    };
    p.append(this.neste);

    this.skinne = el('div', 'plan-skinne');
    this.skinne.setAttribute('role', 'list');
    this.skinne.setAttribute('aria-label', 'Trinn i planprosessen');
    p.append(this.skinne);

    this.kropp = el('div', 'plan-kropp');
    p.append(this.kropp);
    return p;
  }

  private forsteApneVariabel(): FastVariabel {
    const m = manglerForTrinn(this.p, 2);
    return m.find((x) => x.kritisk && x.variabel)?.variabel ?? 'oppdrag';
  }

  // ── Rendring ──────────────────────────────────────────────────────────────
  private render() {
    const f = fremdrift(this.p);

    // Neste handling.
    this.neste.replaceChildren();
    const merke = el('span', 'plan-neste-merke');
    merke.innerHTML = f.nesteHandling ? IKON_VARSEL : IKON_HAKE; // egne SVG-konstanter, ikke feltdata
    const tekst = el('span', 'plan-neste-tekst');
    tekst.textContent = f.nesteHandling ?? 'Alle trinn fullført — planen er klar til gjennomgang.';
    const hint = el('span', 'plan-neste-hint');
    hint.textContent = f.nesteHandling ? `Trinn ${f.trinn}` : 'Ferdig';
    this.neste.classList.toggle('ferdig', !f.nesteHandling);
    this.neste.append(merke, tekst, hint);
    this.neste.title = f.nesteHandling
      ? `Neste handling — gå til trinn ${f.trinn} der dette hører`
      : 'Ingenting kritisk gjenstår';
    this.neste.setAttribute('aria-label', f.nesteHandling
      ? `Neste handling: ${f.nesteHandling}. Gå til trinn ${f.trinn}.`
      : 'Alle trinn fullført — planen er klar til gjennomgang.');

    // Trinn-skinne: fem like brede felt med kortnavn. Fullt navn ligger i
    // aria-label/tooltip og som overskrift i kroppen (§5: ingenting klippes).
    this.skinne.replaceChildren();
    for (const t of UTS_TRINN) {
      const ferdig = trinnFullfort(this.p, t.nr);
      const antall = manglerForTrinn(this.p, t.nr).filter((m) => m.kritisk).length;
      const b = el('button', 'plan-trinn');
      b.setAttribute('role', 'listitem');
      b.classList.toggle('ferdig', ferdig);
      b.classList.toggle('aktiv', this.apentTrinn === t.nr);
      // «naa» = trinnet som eier den gjeldende neste handlingen. Bare dette
      // trinnet får rød teller, så det finnes ett rødt punkt å styre etter.
      b.classList.toggle('naa', !!f.nesteHandling && f.trinn === t.nr);
      if (this.apentTrinn === t.nr) b.setAttribute('aria-current', 'step');
      const topp = el('span', 'plan-trinn-topp');
      const nr = el('span', 'plan-trinn-nr');
      if (ferdig) nr.innerHTML = IKON_HAKE; else nr.textContent = String(t.nr);
      topp.append(nr);
      if (!ferdig && antall) {
        const tel = el('span', 'plan-trinn-tel');
        tel.textContent = String(antall);
        tel.setAttribute('aria-hidden', 'true'); // dekkes av knappens aria-label
        topp.append(tel);
      }
      const nv = el('span', 'plan-trinn-navn');
      nv.textContent = TRINN_KORT[t.nr];
      b.append(topp, nv);
      const status = ferdig ? 'fullført' : antall ? mangeltekst(antall) : 'ingen kritiske mangler';
      b.title = `Trinn ${t.nr} — ${t.navn} (${status}). ${t.hensikt}`;
      b.setAttribute('aria-label', `Trinn ${t.nr} av 5: ${t.navn}, ${status}`);
      b.onclick = () => { this.apentTrinn = t.nr; this.render(); };
      this.skinne.append(b);
    }

    // Kropp: bare det aktive trinnet. Overskriften bærer det FULLE trinnavnet
    // (skinnen har bare plass til kortnavn).
    this.kropp.replaceChildren();
    const def = UTS_TRINN[this.apentTrinn - 1];
    const tittel = el('h3', 'plan-trinn-tittel');
    const tnr = el('span', 'plan-trinn-tittel-nr');
    tnr.textContent = `Trinn ${def.nr} av 5`;
    const tnavn = el('span', '');
    tnavn.textContent = def.navn;
    tittel.append(tnr, tnavn);
    const hensikt = el('p', 'plan-hensikt');
    hensikt.textContent = def.hensikt;
    this.kropp.append(tittel, hensikt);

    if (this.apentTrinn === 1) this.renderTrinn1();
    else if (this.apentTrinn === 2) this.renderTrinn2();
    else if (this.apentTrinn === 3) this.renderTrinn3();
    else if (this.apentTrinn === 4) this.renderTrinn4();
    else this.renderTrinn5();

    this.renderDelprodukter(def.delprodukter);
    this.onStateChange?.();
  }

  // ── Trinn 1 ───────────────────────────────────────────────────────────────
  private renderTrinn1() {
    const g = el('div', 'plan-gruppe');
    g.append(
      this.avkryss('Ordren er lest og forstått', this.p.ordreForstatt, (v) => { this.p.ordreForstatt = v; }),
      this.avkryss('Oppdraget er lest tilbake til overordnet', this.p.tilbakelest, (v) => { this.p.tilbakelest = v; }),
      this.avkryss('Egen avdelings evne til å løse oppdraget er vurdert', this.p.egenEvneVurdert, (v) => { this.p.egenEvneVurdert = v; }),
    );
    this.kropp.append(g);

    // Tidsplan: to tidspunkt inn, fordelingen ut — regnet, ikke skrevet.
    const tid = el('div', 'plan-gruppe');
    tid.append(this.etikett('Tidsplan'));
    const rad = el('div', 'plan-tid-rad');
    // Tidene LESES og SKRIVES som Zulu (UTC) — det sies i etiketten, ellers
    // gjetter operatøren om feltet er lokal tid eller ikke.
    const mottatt = this.tidsfelt('Mottatt (Zulu)', this.p.tidsplan?.mottattTsMs);
    const iverksett = this.tidsfelt('Iverksettes, H-time (Zulu)', this.p.tidsplan?.iverksettTsMs);
    // HVERT FELT SKRIVES FOR SEG. Før krevde denne begge tidspunkt før noe ble
    // lagret, og da lå halve tidsplanen bare i DOM-en — som rives ved trinnbytte.
    // Det var årsaken til «innlagte datoer forsvinner når du går til trinn 2».
    const oppdater = () => {
      const m = lesTid(mottatt), i = lesTid(iverksett);
      const varKomplett = tidsplanKomplett(this.p.tidsplan);
      this.p.tidsplan = (m == null && i == null)
        ? undefined
        : { ...this.p.tidsplan, mottattTsMs: m ?? undefined, iverksettTsMs: i ?? undefined };
      // Re-render BARE når komplettheten endrer seg — det er da fordelingsblokka
      // og mangel-lista skifter. Ellers river vi DOM-en under fingrene på
      // operatøren midt i utfyllingen, og da mistes fokus og halvskrevne felt.
      if (tidsplanKomplett(this.p.tidsplan) !== varKomplett) this.render();
    };
    mottatt.onchange = oppdater; iverksett.onchange = oppdater;
    rad.append(felt('Mottatt (Z)', mottatt), felt('Iverksettes / H-time (Z)', iverksett));
    tid.append(rad);

    if (tidsplanKomplett(this.p.tidsplan)) {
      const f = fordelTid(this.p.tidsplan);
      const boks = el('div', 'plan-tidsfordeling');
      boks.append(
        tall(`${f.totaltMin}`, 'min totalt'),
        tall(`${f.egenPlanleggingMin}`, 'min egen planlegging'),
        tall(`${f.tilUnderavdelingerMin}`, 'min til underavdelingene'),
      );
      tid.append(boks);
      const note = el('p', 'plan-note');
      note.textContent = 'Tredelsregelen (behold 1/3, gi 2/3 videre) er utbredt praksis, '
        + 'ikke en doktrinær grense i håndboka. Juster hvis situasjonen krever det.';
      tid.append(note);
    } else {
      tid.append(this.tom('Sett mottatt tid og H-time for å se hvor mye tid underavdelingene får.'));
    }
    this.kropp.append(tid);

    this.voKnapp(1);
  }

  // ── Trinn 2: akkordeon over de åtte faste variablene ──────────────────────
  private renderTrinn2() {
    const mangler = manglerForTrinn(this.p, 2);
    // Variabelen som eier den FØRSTE kritiske mangelen — den ene som skal ha
    // rød teller (resten er nøytrale tall, ikke alarmer).
    const naaVar = mangler.find((m) => m.kritisk)?.variabel;
    const kron = el('p', 'plan-note');
    kron.textContent = 'Variablene analyseres i denne rekkefølgen — den er doktrinen, ikke en sortering.';
    this.kropp.append(kron);

    const akk = el('div', 'plan-akkordeon');
    for (const [i, v] of FASTE_VARIABLER.entries()) {
      const egne = mangler.filter((m) => m.variabel === v);
      const kritiske = egne.filter((m) => m.kritisk).length;
      const apen = this.apenVariabel === v;

      const rad = el('div', 'plan-var');
      rad.classList.toggle('apen', apen);
      rad.classList.toggle('ferdig', kritiske === 0);
      rad.classList.toggle('naa', naaVar === v);

      const hode = el('button', 'plan-var-hode');
      hode.setAttribute('aria-expanded', String(apen));
      hode.setAttribute('aria-controls', `plan-var-${v}`);
      const nr = el('span', 'plan-var-nr');
      nr.textContent = String(i + 1);
      const nv = el('span', 'plan-var-navn');
      nv.textContent = FAST_VARIABEL_NAVN[v];
      // METT-TC(R)-bokstaven står på hver variabel. Ikke pynt: den forklarer
      // hvorfor rekkefølgen er som den er, og den gjør at en NATO-partner kjenner
      // igjen prosessen uten å lese norsk.
      const bok = el('span', 'plan-var-mettcr');
      bok.textContent = METTCR_BOKSTAV[v];
      bok.title = 'NATO METT-TC(R)';
      bok.setAttribute('aria-hidden', 'true');
      hode.append(nr, nv, bok);
      if (kritiske) {
        const t = el('span', 'plan-var-tel');
        t.textContent = String(kritiske);
        t.setAttribute('aria-hidden', 'true');
        hode.append(t);
      } else {
        const h = el('span', 'plan-var-ok');
        h.innerHTML = IKON_HAKE;
        hode.append(h);
      }
      // Tallet «3» alene er ikke lesbart for skjermleser — hele knappen får navn.
      const st = kritiske ? mangeltekst(kritiske) : 'ingen kritiske mangler';
      hode.setAttribute(
        'aria-label',
        `Variabel ${i + 1} av ${FASTE_VARIABLER.length} (METT-TC ${METTCR_BOKSTAV[v]}): `
        + `${FAST_VARIABEL_NAVN[v]}, ${st}`,
      );
      hode.title = `${FAST_VARIABEL_NAVN[v]} — ${st}`;
      hode.onclick = () => { this.apenVariabel = apen ? null : v; this.render(); };
      rad.append(hode);

      if (apen) {
        const kropp = el('div', 'plan-var-kropp');
        kropp.id = `plan-var-${v}`;
        kropp.setAttribute('role', 'group');
        this.renderVariabel(v, kropp, egne);
        rad.append(kropp);
      }
      akk.append(rad);
    }
    this.kropp.append(akk);
  }

  private renderVariabel(v: FastVariabel, k: HTMLElement, mangler: ReturnType<typeof manglerForTrinn>) {
    const a = this.p.analyse;
    if (v === 'oppdrag') {
      k.append(
        // Rekkefølgen er bevisst: hensikt og oppdrag TO NIVÅ OPP først (konteksten),
        // så oppdraget til OSO (det vi faktisk er tildelt, ordrett), og til slutt
        // egen hensikt (vår utledning). Analysen går utenfra og inn.
        this.tekstfelt('Hensikten to nivå opp', a.hensikt2NivaOpp, (t) => { a.hensikt2NivaOpp = t; },
          'Bærebjelken i oppdragsbasert ledelse — hva vil sjefen to nivå over oppnå?'),
        this.tekstfelt('Oppdraget to nivå opp', a.oppdrag2NivaOpp, (t) => { a.oppdrag2NivaOpp = t; },
          'Hva er tildelt to nivå over oss? Hensikten sier hvorfor, oppdraget sier hva.'),
        this.tekstfelt('Oppdrag til OSO', a.oppdragTilEgenAvdeling, (t) => { a.oppdragTilEgenAvdeling = t; },
          'Oppdraget til Oslofjord sensorområde (HV-01) ORDRETT fra mottatt ordre. '
          + 'Skal aldri smelte sammen med reformuleringen — originalen må finnes å gå tilbake til.'),
        this.tekstfelt('Egen hensikt', a.egenHensikt, (t) => { a.egenHensikt = t; }),
        this.liste('Bindinger', a.bindinger,
          'Hva er vi PÅLAGT å gjøre? «Må.» Hver binding skal gjenfinnes i hver handlemåte.'),
        this.liste('Begrensninger', a.begrensninger,
          'Hva er vi FORHINDRET fra å gjøre? «Må ikke.» En handlemåte som bryter en er ulovlig.'),
        this.oppgaveliste(),
        this.tekstfelt('Reformulert oppdrag', a.reformulertOppdrag, (t) => { a.reformulertOppdrag = t; },
          'Delprodukt: hva vi EGENTLIG skal gjøre, med egne ord.'),
      );
    } else if (v === 'lende') {
      // METT-TC(R): ÉN T dekker terreng OG vær. Rekkefølgen inne i variabelen er
      // terreng først, så vær, så sol — fordi været vurderes MOT lendet, og
      // lysforholdene mot begge. Å ha vær som egen variabel inviterte til å
      // skrive «regn ventes» ett sted og «myrlendt» et annet uten å koble dem.
      k.append(this.sjekkliste('KAVOD — lendet', KAVOD.map((f) => ({
        kode: f.kode, navn: f.navn, sporsmal: f.sporsmal,
        // Bare O og D kan regnes direkte. A og V er taktisk skjønn — det sies.
        kan: /Observasjon|Dekning/.test(f.navn),
        kanTekst: f.terrengStotte,
      })), a.lende.faktorer, 'lende', LENDE_PERSPEKTIV));
      k.append(this.sjekkliste('Været i lendet (VTSSN + vind)', VAERFAKTORER.map((f) => ({
        kode: f.kode, navn: f.navn, sporsmal: f.sporsmal, kan: !!f.fraMet,
        kanTekst: f.fraMet ? 'MET har tallgrunnlag' : undefined,
      })), a.vaer.faktorer, 'vaer'));
      k.append(this.solblokk());
      k.append(this.termiskBlokk());
      k.append(
        this.tekstfelt('Værets påvirkning på lendet (delprodukt)', a.vaer.paavirkning, (t) => { a.vaer.paavirkning = t; }),
        this.tekstfelt('Delkonklusjon lende (delprodukt)', a.lende.delkonklusjon, (t) => { a.lende.delkonklusjon = t; }),
        this.tekstfelt('Lendekalk — referanse til kartprodukt', a.lende.lendekalkRef, (t) => { a.lende.lendekalkRef = t; },
          'Tegn kalken i kartet og noter referansen her.'),
      );
    } else if (v === 'fiende') {
      k.append(
        this.tekstfelt('Trusselvurdering (IPB trinn 3)', a.fiende.trussel, (t) => { a.fiende.trussel = t; }),
        this.liste('Fiendtlige handlemåter (IPB trinn 4)', a.fiende.handlemater,
          'Minst mest sannsynlig + farligste handlemåte.'),
        this.tekstfelt('Delkonklusjon fienden (delprodukt)', a.fiende.delkonklusjon, (t) => { a.fiende.delkonklusjon = t; }),
        this.liste('Etterretningsbehov → innhentingsplan', a.fiende.etterretningsbehov,
          'Hva må vi finne ut, og hvem skal finne det?'),
      );
    } else if (v === 'egneRessurser') {
      k.append(
        this.tekstfelt('Status, støtte, andre egne', a.egneRessurser, (t) => { a.egneRessurser = t; }),
        // Støttebehov er et delprodukt fra trinn 2 (Figur 3). Uten dette feltet
        // hadde delprodukt-brikken «Støttebehov» aldri kunnet bli sann.
        this.liste('Støttebehov (delprodukt)', a.stottebehov,
          'Hva må vi ha fra høyere eller nabo — ild, sanitet, ingeniør, transport?'),
      );
    } else if (v === 'tid') {
      k.append(this.tekstfelt('Tidsanalyse: planlegging, høyere plan, fienden, egen', a.tid, (t) => { a.tid = t; }));
    } else if (v === 'sivileForhold') {
      k.append(this.tekstfelt('Folk og infrastruktur', a.sivileForhold, (t) => { a.sivileForhold = t; },
        'Kjernen i totalforsvaret: befolkning, sivil trafikk, kritisk infrastruktur, nødetater.'));
    } else {
      k.append(
        this.tekstfelt('Taktisk risiko — at oppdraget ikke løses', a.risiko.taktisk, (t) => { a.risiko.taktisk = t; }),
        this.tekstfelt('Ulykkesrisiko — tap uten fiendtlig påvirkning', a.risiko.ulykke, (t) => { a.risiko.ulykke = t; }),
        this.liste('Risikoreduserende tiltak', a.risiko.tiltak,
          'Ett tiltak per risiko du har beskrevet over.'),
      );
    }
    if (mangler.length) {
      const m = el('ul', 'plan-mangler');
      for (const x of mangler) {
        const li = el('li', x.kritisk ? 'kritisk' : '');
        li.textContent = x.hva;
        m.append(li);
      }
      k.append(m);
    }
  }

  // ── Trinn 3 ───────────────────────────────────────────────────────────────
  private renderTrinn3() {
    const metode = el('div', 'plan-gruppe');
    metode.append(this.etikett('Metode for utvikling av handlemåter'));
    const valg = el('div', 'plan-valg');
    for (const m of ['helhetlig', 'analytisk'] as const) {
      const b = el('button', 'plan-chip');
      b.classList.toggle('valgt', this.p.hmMetode === m);
      b.textContent = m === 'helhetlig' ? 'Helhetlig' : 'Analytisk';
      b.setAttribute('aria-pressed', String(this.p.hmMetode === m));
      b.onclick = () => { this.p.hmMetode = m; this.render(); };
      valg.append(b);
    }
    metode.append(valg);
    this.kropp.append(metode);

    const g = el('div', 'plan-gruppe');
    g.append(this.etikett(`Handlemåter (${this.p.handlemater.length})`));
    if (!this.p.handlemater.length) {
      g.append(this.tom('Håndboka forutsetter to til tre PRINSIPIELT forskjellige handlemåter. '
        + 'Tid og risiko avgjør om én er nok.', '+ Ny handlemåte', () => this.nyHm()));
    } else {
      for (const h of this.p.handlemater) g.append(this.hmKort(h));
      const legg = el('button', 'plan-cta');
      legg.textContent = '+ Ny handlemåte';
      legg.onclick = () => this.nyHm();
      g.append(legg);
    }
    this.kropp.append(g);
    this.voKnapp(2);
  }

  private nyHm() {
    const n = this.p.handlemater.length + 1;
    this.p.handlemater.push({ id: `HM${n}`, navn: `Handlemåte ${n}`, beskrivelse: '', fordeler: [], ulemper: [] });
    this.render();
  }

  private hmKort(h: Handlemate): HTMLElement {
    const kort = el('div', 'plan-hm');
    kort.classList.toggle('valgt', this.p.valgtHmId === h.id);
    const hode = el('div', 'plan-hm-hode');
    const navn = document.createElement('input');
    navn.className = 'plan-input plan-hm-navn';
    navn.value = h.navn;
    navn.setAttribute('aria-label', 'Navn på handlemåten');
    navn.onchange = () => { h.navn = navn.value.trim() || h.id; this.render(); };
    hode.append(navn);
    const velg = el('button', 'plan-chip');
    velg.textContent = this.p.valgtHmId === h.id ? 'Valgt' : 'Velg';
    velg.setAttribute('aria-pressed', String(this.p.valgtHmId === h.id));
    velg.setAttribute('aria-label', `Velg ${h.navn} som handlemåte`);
    velg.onclick = () => { this.p.valgtHmId = h.id; this.render(); };
    const slett = el('button', 'plan-slett');
    slett.textContent = '×';
    slett.title = 'Fjern handlemåten';
    slett.setAttribute('aria-label', `Fjern ${h.navn}`);
    slett.onclick = () => {
      this.p.handlemater = this.p.handlemater.filter((x) => x.id !== h.id);
      if (this.p.valgtHmId === h.id) this.p.valgtHmId = undefined;
      this.render();
    };
    hode.append(velg, slett);
    kort.append(hode);
    kort.append(
      this.tekstfelt('Beskrivelse', h.beskrivelse, (t) => { h.beskrivelse = t; }),
      this.tekstfelt('Hva gjør den prinsipielt forskjellig?', h.prinsipp, (t) => { h.prinsipp = t; }),
      this.tekstfelt('Hurtig krigsspill — resultat', h.krigsspill, (t) => { h.krigsspill = t; },
        'Spill handlemåten mot fiendens handlemåter. Uten dette kan trinn 3 ikke fullføres.'),
    );
    return kort;
  }

  // ── Trinn 4 og 5 ──────────────────────────────────────────────────────────
  private renderTrinn4() {
    const g = el('div', 'plan-gruppe');
    g.append(
      this.avkryss('Rekognosering og oppklaring er gjennomført', this.p.rekognoseringGjennomfort, (v) => { this.p.rekognoseringGjennomfort = v; }),
      this.avkryss('Planen er ferdigstilt', this.p.planFerdigstilt, (v) => { this.p.planFerdigstilt = v; }),
    );
    // Å åpne ordrepanelet er IKKE det samme som å ha holdt ordre. Før ble
    // `ordreGittTsMs` satt på klikk, så knappen sa «Ordre gitt ✓» selv om ingen
    // ordre var skrevet eller holdt (§10). Nå er det to handlinger: åpne
    // verktøyet, og krysse av for at ordren faktisk er holdt.
    const ordre = el('button', 'plan-primar');
    ordre.textContent = 'Åpne fempunktsordre';
    ordre.title = 'Åpner ordre-panelet der fempunktsordren skrives';
    ordre.onclick = () => {
      this.hooks.apneOrdre?.();
      this.hooks.varsle?.('Ordren skrives i fempunktsordre-panelet — flytt enheten på kartet for forflytningsordre.');
    };
    g.append(
      this.avkryss('Ordre er holdt for underlagte', !!this.p.ordreGittTsMs, (v) => {
        this.p.ordreGittTsMs = v ? (this.hooks.now?.() ?? Date.now()) : undefined;
      }),
      ordre,
    );
    const note = el('p', 'plan-note');
    note.textContent = 'Fempunktsordren for en forflytning skrives automatisk når du drar enheten '
      + 'på kartet. Ordren arver ORBAT-id-en, så den kan spores til enheten.';
    g.append(note);
    this.kropp.append(g);
  }

  private renderTrinn5() {
    const g = el('div', 'plan-gruppe');
    g.append(
      this.avkryss('Tilbakelesning mottatt fra undergitte sjefer', this.p.tilbakelesningFraUndergitte, (v) => { this.p.tilbakelesningFraUndergitte = v; }),
      this.avkryss('Planen er koordinert', this.p.koordinert, (v) => { this.p.koordinert = v; }),
      this.liste('Justeringer', this.p.justeringer, 'Hva ble endret etter gjennomgangen?'),
    );
    this.kropp.append(g);
  }

  // ── Delprodukter ──────────────────────────────────────────────────────────
  private renderDelprodukter(navn: string[]) {
    const g = el('div', 'plan-delprod');
    g.append(this.etikett('Delprodukter fra trinnet'));
    const rad = el('div', 'plan-chips');
    for (const n of navn) {
      const c = el('span', 'plan-delchip');
      const levert = delproduktLevert(n, this.p);
      c.classList.toggle('mangler', !levert);
      const i = el('span', 'plan-delchip-i');
      i.innerHTML = levert ? IKON_HAKE : IKON_SIRKEL;
      const t = el('span', '');
      t.textContent = n;
      c.append(i, t);
      c.title = levert ? `${n}: levert` : `${n}: ikke levert ennå`;
      rad.append(c);
    }
    g.append(rad);
    this.kropp.append(g);
  }

  private voKnapp(nr: 1 | 2) {
    const sendt = nr === 1 ? this.p.vo1SendtTsMs : this.p.vo2SendtTsMs;
    const g = el('div', 'plan-gruppe');
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'plan-primar';
    // §6: ikoner er SVG. Unicode-haken «✓» blandet tegn og SVG i samme flate.
    if (sendt) {
      const i = el('span', 'plan-primar-i'); i.innerHTML = IKON_HAKE;
      const t = el('span', ''); t.textContent = `Varslingsordre ${nr} sendt`;
      b.append(i, t);
      b.title = `Sendt ${new Date(sendt).toISOString().slice(11, 16)}Z`;
    } else {
      b.textContent = `Send varslingsordre ${nr}`;
    }
    b.disabled = !!sendt;
    b.onclick = () => {
      const ts = this.hooks.now?.() ?? Date.now();
      const v = byggVarslingsordre(nr, this.p, { fra: 'KP', til: 'Underavdelinger', tsMs: ts });
      const res = this.hooks.sendVo?.(varslingsordreTilTekst(v), nr);
      if (res === false) { this.hooks.varsle?.(`Varslingsordre ${nr} kunne ikke sendes — ingen tilkobling til hub.`); return; }
      if (nr === 1) this.p.vo1SendtTsMs = ts; else this.p.vo2SendtTsMs = ts;
      this.hooks.varsle?.(`Varslingsordre ${nr} sendt.`);
      this.render();
    };
    g.append(b);
    if (!this.hooks.sendVo) {
      const n = el('p', 'plan-note');
      n.textContent = 'Sending krever tilkobling til SKADE-hub. Uten den kan ordren bare kopieres.';
      g.append(n);
    }
    this.kropp.append(g);
  }

  // ── Byggeklosser ─────────────────────────────────────────────────────────
  private etikett(t: string): HTMLElement {
    const e = el('span', 'plan-etikett'); e.textContent = t; return e;
  }

  private tom(veiledning: string, cta?: string, run?: () => void): HTMLElement {
    // Designsystem §8: aldri naken grå tekst — ikon + veiledning + evt. CTA.
    const b = el('div', 'plan-tom');
    const i = el('span', 'plan-tom-i'); i.innerHTML = IKON_LISTE;
    const t = el('span', 'plan-tom-t'); t.textContent = veiledning;
    b.append(i, t);
    if (cta && run) {
      const k = el('button', 'plan-cta'); k.textContent = cta; k.onclick = run;
      b.append(k);
    }
    return b;
  }

  private avkryss(tekst: string, verdi: boolean, sett: (v: boolean) => void): HTMLElement {
    const rad = el('label', 'plan-avkryss');
    const boks = document.createElement('input');
    boks.type = 'checkbox'; boks.checked = verdi;
    boks.onchange = () => { sett(boks.checked); this.render(); };
    const t = el('span', ''); t.textContent = tekst;
    rad.append(boks, t);
    return rad;
  }

  private tekstfelt(etikett: string, verdi: string | undefined, sett: (t: string) => void, hjelp?: string): HTMLElement {
    const g = el('div', 'plan-felt');
    const id = nesteId();
    const l = el('label', 'plan-felt-l') as HTMLLabelElement;
    l.textContent = etikett; l.htmlFor = id;
    const inn = document.createElement('textarea');
    inn.className = 'plan-textarea';
    inn.id = id;
    inn.rows = 2;
    inn.value = verdi ?? '';
    inn.onchange = () => { sett(inn.value.trim()); this.render(); };
    g.append(l, inn);
    if (hjelp) {
      const h = el('p', 'plan-hjelp'); h.id = `${id}-h`; h.textContent = hjelp;
      inn.setAttribute('aria-describedby', h.id);
      g.append(h);
    }
    return g;
  }

  private liste(etikett: string, arr: string[], hjelp?: string): HTMLElement {
    const g = el('div', 'plan-felt');
    const l = el('span', 'plan-felt-l'); l.textContent = `${etikett} (${arr.length})`;
    g.append(l);
    const leggTil = () => { arr.push(''); this.render(); };
    // Tom liste = tomtilstand med ikon + veiledning + inline CTA (§8), ikke
    // naken grå hjelpetekst med en løs knapp under.
    if (!arr.length) {
      g.append(this.tom(hjelp ?? `Ingen ${etikett.toLowerCase()} er ført opp ennå.`, '+ Legg til', leggTil));
      return g;
    }
    for (const [i, v] of arr.entries()) {
      const rad = el('div', 'plan-liste-rad');
      const inn = document.createElement('input');
      inn.className = 'plan-input'; inn.value = v;
      inn.setAttribute('aria-label', `${etikett} ${i + 1} av ${arr.length}`);
      inn.onchange = () => { arr[i] = inn.value.trim(); if (!arr[i]) arr.splice(i, 1); this.render(); };
      const x = el('button', 'plan-slett'); x.textContent = '×';
      x.title = 'Fjern linjen';
      x.setAttribute('aria-label', `Fjern ${etikett.toLowerCase()} ${i + 1}`);
      x.onclick = () => { arr.splice(i, 1); this.render(); };
      rad.append(inn, x);
      g.append(rad);
    }
    const legg = el('button', 'plan-cta');
    legg.textContent = '+ Legg til';
    legg.setAttribute('aria-label', `Legg til ${etikett.toLowerCase()}`);
    legg.onclick = leggTil;
    g.append(legg);
    return g;
  }

  private oppgaveliste(): HTMLElement {
    const g = el('div', 'plan-felt');
    g.append(this.etikett('Oppgaver'));
    const typer = [
      ['spesifisert', 'Spesifiserte', 'Står i ordren'],
      ['utledet', 'Utledede', 'Følger av ordren'],
      ['avgjorende', 'Avgjørende', 'Må lykkes'],
    ] as const;
    for (const [type, navn, hjelp] of typer) {
      const egne = this.p.analyse.oppgaver.filter((o) => o.type === type);
      const bolk = el('div', 'plan-oppg-bolk');
      const l = el('span', 'plan-oppg-l');
      l.textContent = `${navn} (${egne.length})`;
      l.title = hjelp;
      bolk.append(l);
      for (const o of egne) {
        const rad = el('div', 'plan-liste-rad');
        const inn = document.createElement('input');
        inn.className = 'plan-input'; inn.value = o.tekst;
        inn.setAttribute('aria-label', `${navn} oppgave`);
        inn.onchange = () => { o.tekst = inn.value.trim(); this.render(); };
        const x = el('button', 'plan-slett'); x.textContent = '×';
        x.title = 'Fjern oppgaven';
        x.setAttribute('aria-label', `Fjern ${navn.toLowerCase()} oppgave`);
        x.onclick = () => {
          this.p.analyse.oppgaver = this.p.analyse.oppgaver.filter((y) => y.id !== o.id);
          this.render();
        };
        rad.append(inn, x);
        bolk.append(rad);
      }
      const legg = el('button', 'plan-cta');
      legg.textContent = `+ ${navn.toLowerCase().replace(/e$/, '')} oppgave`;
      legg.onclick = () => {
        this.p.analyse.oppgaver.push({ id: `${type}-${this.p.analyse.oppgaver.length + 1}`, type, tekst: '' });
        this.render();
      };
      bolk.append(legg);
      g.append(bolk);
    }
    return g;
  }

  /** VTSSN/KAVOD som rader: spørsmål + felt + «hent» der maskinen kan svare. */
  /**
   * SOL OG TUSSMØRKE — regnet, ikke skrevet inn.
   *
   * Eierens spørsmål var «kan det hentes opp automatisk ved innleggelse av dato».
   * Svaret er ja, og uten nett: soltider er ren astronomi og regnes eksakt av
   * `sunTimes()` for enhver dato og posisjon. Det er en viktig forskjell fra
   * VÆRET over, som er en PROGNOSE med kort holdbarhet — derfor står de i to
   * ulike blokker med ulik merking, og ikke i samme tabell.
   *
   * BMNT/EENT er tatt med fordi det er tidene en plan faktisk henger på: nautisk
   * tussmørke (sol −12°) er mørkevinduet, ikke soloppgang. En operatør som
   * planlegger et fremrykk «i mørket» og bruker solnedgang, har ~40 minutter
   * lys han ikke har regnet med.
   */
  private solblokk(): HTMLElement {
    const rot = el('div', 'plan-sol');
    const hode = el('div', 'plan-sol-hode');
    hode.textContent = 'Sol og tussmørke — regnet automatisk';
    rot.append(hode);

    const pos = this.hooks.hentPosisjon?.() ?? null;
    const a = this.p.analyse;

    // Datovelger. Standard er operasjonsdatoen om den finnes, ellers i dag.
    const rad = el('label', 'plan-sol-dato');
    rad.append(document.createTextNode('Dato: '));
    const felt = el('input', 'plan-inn') as HTMLInputElement;
    felt.type = 'date';
    felt.value = a.solDato ?? new Date(this.hooks.now?.() ?? Date.now()).toISOString().slice(0, 10);
    felt.addEventListener('change', () => {
      a.solDato = felt.value || undefined;
      this.render();
    });
    rad.append(felt);
    rot.append(rad);

    if (!pos) {
      // Ingen posisjon → ingen tider. Å vise tider for et gjettet punkt ville
      // vært verre enn å vise ingenting: sol står opp ~40 min senere i Kirkenes
      // enn i Kristiansand samme dag, og feilen er usynlig i tallene.
      const m = el('p', 'plan-sol-mangler');
      m.textContent = 'Kartet har ingen posisjon ennå — soltider kan ikke regnes. '
        + 'Åpne kartet, så fylles blokka.';
      rot.append(m);
      return rot;
    }

    const d = new Date(`${felt.value}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) {
      const m = el('p', 'plan-sol-mangler');
      m.textContent = 'Ugyldig dato.';
      rot.append(m);
      return rot;
    }

    const t = sunTimes(d, pos.lat, pos.lon);
    const klokke = (x: Date | null): string =>
      x ? x.toISOString().slice(11, 16) + 'Z' : '—';

    const tabell = el('div', 'plan-sol-tider');
    for (const [navn, verdi, merk] of [
      ['BMNT (mørket slutter)', klokke(t.bmnt), 'nautisk tussmørke, sol −12° — dette er mørkevinduet'],
      ['Soloppgang', klokke(t.sunrise), ''],
      ['Solmiddag', klokke(t.solarNoon), 'sol i sør — gir også skyggeretning'],
      ['Solnedgang', klokke(t.sunset), ''],
      ['EENT (mørket starter)', klokke(t.eent), 'nautisk tussmørke, sol −12°'],
    ] as [string, string, string][]) {
      const r = el('div', 'plan-sol-rad');
      const n = el('span', 'plan-sol-navn');
      n.textContent = navn;
      const v = el('span', 'plan-sol-verdi');
      v.textContent = verdi;
      r.append(n, v);
      if (merk) r.title = merk;
      tabell.append(r);
    }
    rot.append(tabell);

    if (!t.sunrise || !t.sunset) {
      // Midnattssol eller polarnatt er ikke en feil — det er et faktum som
      // endrer hele planen, og det skal stå med ord.
      const m = el('p', 'plan-sol-merk');
      m.textContent = t.bmnt || t.eent
        ? 'Sola går ikke opp eller ned denne datoen på denne breddegraden.'
        : 'Midnattssol eller polarnatt — ingen tussmørkeovergang i det hele tatt.';
      rot.append(m);
    }

    const kilde = el('p', 'plan-sol-kilde');
    kilde.textContent = `Regnet for kartsenteret (${pos.lat.toFixed(3)}, ${pos.lon.toFixed(3)}). `
      + 'Astronomi — ikke prognose, og krever ikke samband. Tider i UTC (Z).';
    rot.append(kilde);
    return rot;
  }

  /**
   * ANBEFALT TERMISK INNHENTINGSVINDU (GEO-15) — samme punkt/dato som
   * `solblokk()`, bygget på @skade/terrain sin `termiskInnhentingsvindu()`.
   *
   * Dette er en ANBEFALING, ikke en måling (grunnlov §1): motoren regner
   * KUN på soltider (solmiddag/-nedgang/-oppgang), ikke faktisk temperatur.
   * Midt på dagen varmes lavspesifikk-varmekapasitet-underlag (asfalt, tak)
   * raskere enn et menneske, og kontrasten kollapser; rundt soloppgang og
   * solnedgang KRYSSER bakgrunn og mål temperatur og kontrasten faller mot
   * null i et par timer. Vinduet under står derfor ALLTID sammen med
   * `grunnlag` (soltidene det er utledet fra) og `forbehold` (hva motoren
   * ikke vet) — se `termisk.ts` for den fulle begrunnelsen.
   */
  private termiskBlokk(): HTMLElement {
    const rot = el('div', 'plan-termisk');
    const hode = el('div', 'plan-sol-hode');
    hode.textContent = 'Anbefalt termisk innhentingsvindu — regnet automatisk';
    rot.append(hode);

    const pos = this.hooks.hentPosisjon?.() ?? null;
    const a = this.p.analyse;
    if (!pos) {
      const m = el('p', 'plan-sol-mangler');
      m.textContent = 'Kartet har ingen posisjon ennå — termisk vindu kan ikke regnes. '
        + 'Åpne kartet, så fylles blokka.';
      rot.append(m);
      return rot;
    }

    const datoStr = a.solDato ?? new Date(this.hooks.now?.() ?? Date.now()).toISOString().slice(0, 10);
    const d = new Date(`${datoStr}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) {
      const m = el('p', 'plan-sol-mangler');
      m.textContent = 'Ugyldig dato.';
      rot.append(m);
      return rot;
    }

    const v = termiskInnhentingsvindu(d.getTime(), pos.lat, pos.lon);
    const klokke = (ms: number): string => new Date(ms).toISOString().slice(11, 16) + 'Z';
    const visVindu = (w: TermiskVindu): string => `${klokke(w.startMs)}–${klokke(w.sluttMs)}`;

    const anbefalt = el('div', 'plan-termisk-anbefalt');
    if (v.anbefaltVindu.length) {
      const etikett = el('span', 'plan-termisk-etikett');
      etikett.textContent = 'Anbefalt: ';
      const verdi = el('span', 'plan-termisk-verdi');
      verdi.textContent = v.anbefaltVindu.map(visVindu).join(', ');
      anbefalt.append(etikett, verdi);
    } else {
      anbefalt.textContent = 'Ingen anbefalt vindu denne datoen/posisjonen (se forklaring under).';
    }
    rot.append(anbefalt);

    if (v.unngaaVindu.length) {
      const unngaa = el('div', 'plan-termisk-unngaa');
      const etikett = el('span', 'plan-termisk-etikett');
      etikett.textContent = 'Unngå: ';
      const verdi = el('span', 'plan-termisk-verdi');
      verdi.textContent = v.unngaaVindu.map(visVindu).join(', ');
      unngaa.append(etikett, verdi);
      rot.append(unngaa);
    }

    const grunnlag = el('p', 'plan-sol-merk');
    grunnlag.textContent = v.grunnlag;
    rot.append(grunnlag);

    // Forbeholdet er IKKE valgfritt utstyr — det står alltid, uansett om et
    // vindu ble funnet eller ikke (samme regel som GEO-08s forbehold-visning).
    const forbehold = el('p', 'plan-sol-kilde');
    forbehold.textContent = v.forbehold;
    rot.append(forbehold);

    return rot;
  }

  /**
   * VTSSN/KAVOD som rader. `perspektiver` gir ETT felt per perspektiv i stedet for
   * ett felt totalt — se `LENDE_PERSPEKTIV` i @skade/hildr. Værfaktorene har ingen
   * perspektiver (regn er regn for begge parter); lendefaktorene har to, fordi
   * samme bekk er et hinder for angriperen og en flankesikring for forsvareren.
   */
  private sjekkliste(
    navn: string,
    faktorer: { kode: string; navn: string; sporsmal: string; kan: boolean; kanTekst?: string }[],
    lager: Record<string, string> | Record<string, Partial<Record<string, string>>>,
    slag: 'vaer' | 'lende',
    perspektiver?: readonly string[],
  ): HTMLElement {
    const g = el('div', 'plan-felt');
    g.append(this.etikett(navn));
    for (const f of faktorer) {
      const rad = el('div', 'plan-sjekk');
      const kode = el('span', 'plan-sjekk-kode');
      kode.textContent = f.kode;
      kode.setAttribute('aria-hidden', 'true');
      const midt = el('div', 'plan-sjekk-midt');
      // Handlingen (hent / taktisk skjønn) hører til RADEN, ikke til feltet, og
      // står derfor på samme linje som faktornavnet. Under inputen leste den
      // som en send-knapp for det man nettopp skrev.
      const topp = el('div', 'plan-sjekk-topp');
      const tittel = el('span', 'plan-sjekk-navn');
      tittel.textContent = f.navn;
      topp.append(tittel);
      const sp = el('span', 'plan-sjekk-sp');
      sp.textContent = f.sporsmal;

      if (f.kan) {
        const hent = el('button', 'plan-hent') as HTMLButtonElement;
        // Ikonet skal si HVOR underlaget kommer fra: sky = MET, fjell = terreng.
        const ikon = el('span', 'plan-hent-i');
        ikon.innerHTML = slag === 'vaer' ? IKON_VAER : IKON_TERRENG;
        const lbl = el('span', ''); lbl.textContent = 'Hent';
        hent.append(ikon, lbl);
        hent.title = f.kanTekst ?? 'Hent underlag';
        hent.setAttribute('aria-label', `Hent underlag for ${f.navn}`);
        hent.onclick = async () => {
          hent.classList.add('laster');
          hent.disabled = true;
          try {
            const svar = slag === 'lende'
              ? await this.hooks.hentLende?.(f.navn)
              : await this.hentVaerFaktor(f.navn);
            if (svar) { lager[f.navn] = svar; this.render(); return; }
            this.hooks.varsle?.(`Fant ikke underlag for ${f.navn} — fyll ut manuelt.`);
          } catch { this.hooks.varsle?.(`Kunne ikke hente underlag for ${f.navn}.`); }
          hent.classList.remove('laster');
          hent.disabled = false;
        };
        topp.append(hent);
      } else {
        // Ærlighet (designsystem §10): si at dette er skjønn, ikke la det se ut
        // som et felt som venter på en beregning. Tooltipen sa før hva
        // terrengmotoren KAN bidra med — det leste som et løfte om en beregning
        // det ikke finnes knapp for. Nå sier den først at dette er skjønn.
        const s = el('span', 'plan-skjonn');
        s.textContent = 'Taktisk skjønn';
        s.title = 'Taktisk skjønn — verktøyet kan ikke regne dette ut.'
          + (f.kanTekst ? ` Underlag som finnes: ${f.kanTekst}` : '');
        topp.append(s);
      }

      midt.append(topp, sp);
      if (!perspektiver) {
        const enkel = lager as Record<string, string>;
        const inn = document.createElement('input');
        inn.className = 'plan-input';
        inn.value = enkel[f.navn] ?? '';
        inn.placeholder = 'Vurdering …';
        inn.setAttribute('aria-label', `${f.navn}: ${f.sporsmal}`);
        inn.onchange = () => { enkel[f.navn] = inn.value.trim(); this.render(); };
        midt.append(inn);
      } else {
        const nestet = lager as Record<string, Partial<Record<string, string>>>;
        for (const pers of perspektiver) {
          const par = el('div', 'plan-pers');
          const merk = el('span', 'plan-pers-merke');
          merk.textContent = LENDE_PERSPEKTIV_NAVN[pers as never] ?? pers;
          const inn = document.createElement('input');
          inn.className = 'plan-input';
          inn.value = nestet[f.navn]?.[pers] ?? '';
          inn.placeholder = 'Vurdering …';
          // Perspektivet MÅ være i aria-label. «Vurdering» to ganger på rad uten
          // å si hvilket ståsted er ubrukelig for skjermleser — og for øyet er
          // merket til venstre nok, men det er ikke i tilgjengelighetstreet.
          inn.setAttribute(
            'aria-label',
            `${f.navn}, ${LENDE_PERSPEKTIV_NAVN[pers as never] ?? pers}: ${f.sporsmal}`,
          );
          inn.onchange = () => {
            const gjeldende = nestet[f.navn] ?? {};
            gjeldende[pers] = inn.value.trim();
            nestet[f.navn] = gjeldende;
            this.render();
          };
          par.append(merk, inn);
          midt.append(par);
        }
      }
      rad.append(kode, midt);
      g.append(rad);
    }
    return g;
  }

  private async hentVaerFaktor(navn: string): Promise<string | null> {
    const v = await this.hooks.hentVaer?.();
    if (!v) return null;
    const n = (k: string) => (typeof v[k] === 'number' ? (v[k] as number) : null);
    if (navn === 'Temperatur') { const t = n('airTemperature'); return t == null ? null : `${Math.round(t)} °C (MET)`; }
    if (navn === 'Skydekke') { const c = n('cloudAreaFraction'); return c == null ? null : `${Math.round(c)} % skydekke (MET)`; }
    if (navn === 'Nedbør') { const p = n('precipitationNext1h'); return p == null ? null : `${p.toFixed(1)} mm neste time (MET)`; }
    if (navn.startsWith('Vind')) {
      const ms = n('windSpeed');
      if (ms == null) return null;
      const b = vindstyrke(ms);
      const fra = n('windFromDirection');
      // «FRA» skrives ut. MET-feltet heter `wind_from_direction`, og en operatør
      // som leser «vind 270°» og tror det betyr «blåser mot vest» får
      // lyddekningen speilvendt.
      const retning = fra == null
        ? 'retning ukjent'
        : `fra ${vindretningNavn(fra)} (${Math.round(fra)}°)`;
      return `${ms.toFixed(1)} m/s — ${b.navn} (Beaufort ${b.beaufort}), ${retning}. ${b.konsekvens} (MET)`;
    }
    const s = typeof v.symbolNext1h === 'string' ? v.symbolNext1h : null;
    return s ? `${s} (MET)` : null;
  }

  private tidsfelt(navn: string, verdi?: number): HTMLInputElement {
    const i = document.createElement('input');
    i.type = 'datetime-local';
    i.className = 'plan-input plan-tid';
    i.setAttribute('aria-label', navn);
    if (verdi != null) i.value = new Date(verdi).toISOString().slice(0, 16);
    return i;
  }
}

// ── Små hjelpere ────────────────────────────────────────────────────────────
let idTeller = 0;
/** Unik id så <label for> kan kobles til kontrollen (klikk på etikett = fokus). */
function nesteId(): string { return `plan-f${++idTeller}`; }

/** Norsk entall/flertall — «1 kritiske mangler» leses som en feil i verktøyet. */
export function mangeltekst(antall: number): string {
  return antall === 1 ? '1 kritisk mangel' : `${antall} kritiske mangler`;
}

function el(tag: string, cls: string): HTMLElement {
  const e = document.createElement(tag); if (cls) e.className = cls;
  // Knapper utenfor form: type=button så de aldri kan submitte noe.
  if (tag === 'button') (e as HTMLButtonElement).type = 'button';
  return e;
}
function spenn(cls: string, t: string): HTMLElement {
  const e = el('span', cls); e.textContent = t; return e;
}
function knapp(cls: string, t: string, tittel: string, run: () => void): HTMLElement {
  const b = el('button', cls);
  b.textContent = t; b.title = tittel;
  b.setAttribute('aria-label', tittel);
  b.onclick = run;
  return b;
}
function felt(navn: string, inn: HTMLElement): HTMLElement {
  const g = el('div', 'plan-felt');
  const id = inn.id || nesteId();
  inn.id = id;
  const l = el('label', 'plan-felt-l') as HTMLLabelElement;
  l.textContent = navn; l.htmlFor = id;
  g.append(l, inn);
  return g;
}
function tall(verdi: string, etikett: string): HTMLElement {
  const b = el('div', 'plan-tall');
  const v = el('span', 'plan-tall-v'); v.textContent = verdi;
  const e = el('span', 'plan-tall-e'); e.textContent = etikett;
  b.append(v, e);
  return b;
}
function lesTid(i: HTMLInputElement): number | null {
  if (!i.value) return null;
  const ms = Date.parse(i.value + 'Z');
  return Number.isFinite(ms) ? ms : null;
}
